// trameria's fenced blocks (```mermaid): drawn as what they describe, with the
// source one press away. The node keeps the source as written (`payload`); the
// only write is `updateAttributes({ payload })`.
//
// Mermaid is imported when the first diagram is drawn (it is large, and most
// documents have none). A diagram that cannot be drawn shows its source and
// the reason, with the source editable in place (`FenceSource`).
import type { NodeViewProps } from "@tiptap/core";
import { NodeViewWrapper, ReactNodeViewRenderer } from "@tiptap/react";
import { blockIdDomAttrs, TrameriaFence } from "@trameria/editor";
import { m } from "@trameria/i18n";
import {
  type KeyboardEvent as ReactKeyboardEvent,
  useEffect,
  useId,
  useRef,
  useState,
} from "react";
import { useIsDark } from "../../device/theme";
import { Button } from "../../ui/button";

type Mermaid = typeof import("mermaid")["default"];

/** One load per page (a module, not app state: the library itself is global). */
let mermaidLoad: Promise<Mermaid> | null = null;
function loadMermaid(): Promise<Mermaid> {
  mermaidLoad ??= import("mermaid").then((mod) => mod.default);
  return mermaidLoad;
}

/**
 * Draws a mermaid diagram. On failure the source shows instead, with the
 * reason and a way to fix the source.
 *
 * `suppressErrorRendering: true`: without it mermaid leaves its own "bomb"
 * error SVG - with its temporary DOM - directly under `document.body`
 * (outside this component) and throws. With it, it touches no DOM and only
 * rejects, so a failure leaves nothing behind outside this block.
 *
 * The theme follows the screen's ("neutral" on light, "dark" on dark): a
 * light diagram on the dark body glared.
 */
export function MermaidDiagram({
  payload,
  editable,
  onChangePayload,
}: {
  payload: string;
  editable: boolean;
  onChangePayload: (next: string) => void;
}) {
  const container = useRef<HTMLDivElement>(null);
  const [error, setError] = useState<string | null>(null);
  const renderId = useId().replace(/[^a-zA-Z0-9_-]/g, "");
  const dark = useIsDark();

  // frontend-development: not a fetch; draws the diagram with a library loaded on demand.
  useEffect(() => {
    let cancelled = false;
    setError(null);
    const src = payload.trim();
    if (src === "") {
      if (container.current) container.current.innerHTML = "";
      return;
    }
    // The diagram's ground is the block's (a surface token): edge labels mask
    // the line under them with it, and mermaid's own grey showed as patches
    // on the dark body. Mermaid needs a real color, so the token is resolved.
    const surface = getComputedStyle(document.documentElement)
      .getPropertyValue("--color-surface")
      .trim();
    loadMermaid()
      .then(async (mermaid) => {
        mermaid.initialize({
          startOnLoad: false,
          theme: dark ? "dark" : "neutral",
          suppressErrorRendering: true,
          securityLevel: "strict",
          ...(surface.startsWith("#")
            ? {
                themeVariables: {
                  background: surface,
                  edgeLabelBackground: surface,
                },
              }
            : {}),
        });
        const { svg } = await mermaid.render(
          `trameria-mermaid-${renderId}`,
          src,
        );
        if (!cancelled && container.current) container.current.innerHTML = svg;
      })
      .catch((err: unknown) => {
        if (!cancelled)
          setError(err instanceof Error ? err.message : String(err));
      });
    return () => {
      cancelled = true;
    };
  }, [payload, renderId, dark]);

  if (error !== null) {
    return (
      <FenceSource
        payload={payload}
        editable={editable}
        reason={m.blocks_mermaid_failed({ error })}
        onSave={onChangePayload}
      />
    );
  }
  return (
    <div className="trameria-mermaid" contentEditable={false}>
      <div ref={container} data-mermaid-diagram="" />
    </div>
  );
}

/**
 * The source of a fence that could not be drawn: the reason, the source as
 * text, and (when writable) "Edit source" to fix it in a text field. Saving
 * writes the payload back; the diagram is tried again.
 */
export function FenceSource({
  payload,
  editable,
  reason,
  onSave,
}: {
  payload: string;
  editable: boolean;
  reason: string;
  onSave: (next: string) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(payload);
  if (editing) {
    const save = () => {
      onSave(draft);
      setEditing(false);
    };
    return (
      <div className="trameria-fence-source" contentEditable={false}>
        <textarea
          aria-label={m.blocks_fence_source()}
          className="plain-field block w-full resize-y border-none bg-transparent p-0 font-mono text-[12.5px] text-body outline-none"
          rows={Math.min(Math.max(draft.split("\n").length, 3), 16)}
          value={draft}
          // biome-ignore lint/a11y/noAutofocus: opened by pressing "Edit source"; typing continues there
          autoFocus
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e: ReactKeyboardEvent<HTMLTextAreaElement>) => {
            if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
              e.preventDefault();
              save();
            } else if (e.key === "Escape") {
              e.preventDefault();
              setDraft(payload);
              setEditing(false);
            }
          }}
        />
        <div className="mt-2 flex gap-2">
          <Button variant="primary" onClick={save}>
            {m.blocks_fence_save()}
          </Button>
          <Button
            variant="ghost"
            onClick={() => {
              setDraft(payload);
              setEditing(false);
            }}
          >
            {m.blocks_fence_cancel()}
          </Button>
        </div>
      </div>
    );
  }
  return (
    <div className="trameria-fence-source" contentEditable={false}>
      <p className="m-0 mb-1.5 text-[12px] text-danger">{reason}</p>
      <pre className="m-0 whitespace-pre-wrap font-mono text-[12.5px] text-body">
        {payload}
      </pre>
      {editable && (
        <div className="mt-2">
          <Button
            variant="outline"
            onClick={() => {
              setDraft(payload);
              setEditing(true);
            }}
          >
            {m.blocks_fence_edit_source()}
          </Button>
        </div>
      )}
    </div>
  );
}

function FenceView({ node, editor, updateAttributes }: NodeViewProps) {
  const payload = String(node.attrs.payload ?? "");
  return (
    // A node view's DOM does not go through renderHTML: the block id is set
    // here, or block hit areas would miss fences.
    <NodeViewWrapper
      as="div"
      data-trameria-fence-kind={String(node.attrs.kind)}
      {...blockIdDomAttrs(node)}
      contentEditable={false}
    >
      <MermaidDiagram
        payload={payload}
        editable={editor.isEditable}
        onChangePayload={(next) => updateAttributes({ payload: next })}
      />
    </NodeViewWrapper>
  );
}

/**
 * The editor's fence with this node view. Same name; the markdown mapping of
 * kind and payload is inherited unchanged.
 */
export const FenceWithView = TrameriaFence.extend({
  addNodeView() {
    return ReactNodeViewRenderer(FenceView, {
      // Clicks and keys inside the source field belong to it, not to ProseMirror.
      stopEvent: ({ event }) =>
        event.target instanceof Element &&
        event.target.closest("textarea, button") !== null,
    });
  },
});
